import { useEffect, type ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  PRACTICE,
  buildReport,
  countWord,
  usd2,
  type ReportLine,
  type ReportModel,
} from '../model';
import '../eligibility-report.css';

/**
 * The printed eligibility report (rcm-28). A rendered page on its own route, not
 * window.print() of the app: no frame, no sidebar, no controls on the sheet.
 * The toolbar above the sheet is screen-only; print CSS hides it.
 */
export function EligibilityReportPage() {
  const { patientId } = useParams<{ patientId: string }>();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  // ?visit= prints that exact scheduled visit (the calendar modal's link); otherwise the patient's own.
  const report = buildReport(patientId, search.get('visit'));
  const title = report ? `Eligibility estimate — ${report.patientName}` : 'Eligibility estimate';

  useEffect(() => {
    const previous = document.title;
    document.title = title;
    return () => {
      document.title = previous;
    };
  }, [title]);

  const onBack = () => {
    if (window.history.length > 1) void navigate(-1);
    else void navigate(`/patients/${patientId ?? ''}`);
  };

  return (
    <main className="elr-ground">
      <div className="elr-bar" role="toolbar" aria-label="Report actions">
        <button type="button" className="elr-btn" onClick={onBack}>
          Back
        </button>
        {report ? (
          <button
            type="button"
            className="elr-btn elr-btn-primary"
            onClick={() => {
              window.print();
            }}
          >
            Print
          </button>
        ) : null}
      </div>
      {report ? <Sheet r={report} /> : <NotFound />}
    </main>
  );
}

export default EligibilityReportPage;

function Masthead() {
  return (
    <div className="elr-top">
      <div>
        <div className="elr-logo">
          Elva<span>RCM</span>
        </div>
        <div className="elr-prepared">Prepared for {PRACTICE.name}</div>
      </div>
      <div className="elr-addr">
        {PRACTICE.name}
        <br />
        {PRACTICE.address}
        <br />
        {PRACTICE.phone} · NPI <span className="elr-mono">{PRACTICE.npi}</span>
      </div>
    </div>
  );
}

function Sheet({ r }: { r: ReportModel }) {
  const isNoInsurance = r.state === 'no_insurance';
  return (
    <article className="elr-sheet" aria-labelledby="elr-title">
      <Masthead />

      <h1 id="elr-title">{isNoInsurance ? 'No insurance on file' : `Insurance estimate for ${r.patientName}`}</h1>
      <p className="elr-sub">
        {isNoInsurance ? `For ${r.patientName}. ` : ''}
        {r.visit ? `Visit on ${r.visit.dateLong} at ${r.visit.time} with ${r.visit.provider}` : 'No visit on the schedule'}
      </p>

      <Facts r={r} />
      <Amount r={r} />

      {r.lines.length > 8 ? (
        <div className="elr-break">
          <Masthead />
        </div>
      ) : null}
      <Procedures r={r} />
      <PlanCards r={r} />
      <Opportunities r={r} />
      <Sources r={r} />
      <Footer r={r} />
    </article>
  );
}

function Facts({ r }: { r: ReportModel }) {
  const n = r.sourceCount;
  const unchecked = r.state === 'no_insurance' ? 'Not checked' : 'Not yet';
  return (
    <div className="elr-facts">
      <div>
        <div className="elr-lab">Patient</div>
        <div className="elr-v">{r.patientName}</div>
        <div className="elr-s">
          DOB {r.dob} · chart <span className="elr-mono">{r.chart}</span>
        </div>
      </div>
      <div>
        <div className="elr-lab">Plan</div>
        {r.plan ? (
          <>
            <div className="elr-v">{r.plan.payer}</div>
            <div className="elr-s">
              {r.plan.type} · {r.plan.network}
            </div>
          </>
        ) : (
          <>
            <div className="elr-v">None on file</div>
            <div className="elr-s">No plan to check</div>
          </>
        )}
      </div>
      <div>
        <div className="elr-lab">Member ID</div>
        {r.memberId ? (
          <>
            <div className="elr-v elr-mono">{r.memberId}</div>
            <div className="elr-s">Group not on file</div>
          </>
        ) : (
          <>
            <div className="elr-v">—</div>
            <div className="elr-s">None on file</div>
          </>
        )}
      </div>
      <div>
        <div className="elr-lab">Coverage checked</div>
        {r.checkedLabel ? (
          <>
            <div className="elr-v">{r.checkedLabel}</div>
            <div className="elr-s">
              {r.checkFailed ? 'Last check failed' : n !== null ? `from ${String(n)} sources` : ''}
            </div>
          </>
        ) : (
          <>
            <div className="elr-v">{unchecked}</div>
            <div className="elr-s">
              {r.state === 'no_insurance' ? 'No plan on file' : 'Coverage has not been checked yet'}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Amount({ r }: { r: ReportModel }) {
  const first = r.firstName;
  if (r.state === 'no_insurance') {
    return (
      <p className="elr-note-plain">
        ELVA has no plan on file for {r.patientName}, so it cannot estimate what insurance will pay. The fees below are
        this practice’s own fees.
      </p>
    );
  }
  const payer = r.plan?.payerShort ?? 'the plan';
  const fees = usd2(r.totals.fee);

  let big: string;
  let bigClass = 'elr-big';
  let body: ReactNode;

  if (r.state === 'verified') {
    big = usd2(r.pays.patient);
    body = (
      <>
        Allowed amount <b>{usd2(r.totals.allowed)}</b> &minus; {payer} pays <b>{usd2(r.pays.plan)}</b> ={' '}
        <b>{usd2(r.pays.patient)}</b>. Your fees come to {fees}
        {r.writeOff > 0 ? (
          <>
            ; <b>{usd2(r.writeOff)}</b> is the in-network write-off and is not billed to the patient
          </>
        ) : null}
        .
      </>
    );
  } else if (r.state === 'pre_auth') {
    big = 'Not yet known';
    body = (
      <>
        {payer} has not answered the pre-auth, so neither {payer}’s share nor {first}’s share is printed. Your fees come
        to {fees}.
      </>
    );
  } else if (r.state === 'coverage_ended') {
    big = `Coverage ended ${r.coverageEnded ?? 'on file'}`;
    bigClass = 'elr-big elr-big-sm';
    body = <>No estimate is printed for a plan that has ended. Your fees come to {fees}.</>;
  } else {
    big = 'Coverage has not been checked yet';
    bigClass = 'elr-big elr-big-sm';
    body = <>No amount is printed until the benefits check is done. Your fees come to {fees}.</>;
  }

  return (
    <section className="elr-hero" aria-label="Estimate">
      <div className="elr-lab elr-lab-teal">{first} pays today, estimated</div>
      <div className={bigClass}>{big}</div>
      <p className="elr-eqn">{body}</p>
    </section>
  );
}

function LineRow({ l, cols }: { l: ReportLine; cols: boolean }) {
  return (
    <tr>
      <td>
        {l.cdt ? (
          <>
            <span className="elr-mono elr-code">{l.cdt}</span>&nbsp;{' '}
          </>
        ) : null}
        {l.label}
      </td>
      <td className="elr-num">{usd2(l.fee)}</td>
      {cols ? (
        <>
          <td className="elr-num">{l.allowed !== null ? usd2(l.allowed) : 'Not yet known'}</td>
          <td className="elr-num">{l.plan !== null ? usd2(l.plan) : 'Not yet known'}</td>
          <td className="elr-num">{l.patient !== null ? usd2(l.patient) : 'Not yet known'}</td>
        </>
      ) : null}
    </tr>
  );
}

function Procedures({ r }: { r: ReportModel }) {
  const payer = r.plan?.payerShort ?? 'Plan';
  const first = r.firstName;
  const cols = r.showAmountColumns;
  const amountsKnown = r.state === 'verified';
  return (
    <>
      <h2>Today’s procedures</h2>
      <table className="elr-table">
        <thead>
          <tr>
            <th>Procedure</th>
            <th>Your fee</th>
            {cols ? (
              <>
                <th>Allowed</th>
                <th>{payer} pays</th>
                <th>{first} pays</th>
              </>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {r.lines.map((l) => (
            <LineRow key={l.key} l={l} cols={cols} />
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td>{r.cap ? 'Totals before the yearly maximum' : 'Totals'}</td>
            <td className="elr-num">{usd2(r.totals.fee)}</td>
            {cols ? (
              amountsKnown ? (
                <>
                  <td className="elr-num">{usd2(r.totals.allowed)}</td>
                  <td className="elr-num">{usd2(r.totals.plan)}</td>
                  <td className="elr-num">{usd2(r.totals.patient)}</td>
                </>
              ) : (
                <>
                  <td className="elr-num">Not yet known</td>
                  <td className="elr-num">Not yet known</td>
                  <td className="elr-num">Not yet known</td>
                </>
              )
            ) : null}
          </tr>
        </tfoot>
      </table>
      {r.cap ? (
        <div className="elr-note">
          <b>Capped by what the plan has left.</b> {payer} would pay {usd2(r.cap.wouldPay)}, but only {usd2(r.cap.left)}{' '}
          of the yearly maximum is left, so {first} pays {usd2(r.pays.patient)} instead of{' '}
          {usd2(r.cap.uncappedPatient)}.
        </div>
      ) : null}
    </>
  );
}

function PlanCards({ r }: { r: ReportModel }) {
  if (!r.yearly && !r.ortho && !r.deductible && !r.planYear) return null;
  return (
    <>
      <h2>This plan</h2>
      <div className="elr-pf">
        <div>
          <div className="elr-lab">Yearly maximum</div>
          <div className="elr-v">{r.yearly ? r.yearly.value : 'Not on file'}</div>
          <div className="elr-s">{r.yearly ? r.yearly.sub : 'Not returned by the check'}</div>
        </div>
        <div>
          <div className="elr-lab">Deductible</div>
          <div className="elr-v">{r.deductible ? r.deductible.value : 'Not on file'}</div>
          <div className="elr-s">{r.deductible ? r.deductible.sub : 'Not returned by the check'}</div>
        </div>
        <div>
          <div className="elr-lab">Orthodontic max</div>
          <div className="elr-v">{r.ortho ? r.ortho.value : 'Not on file'}</div>
          <div className="elr-s">{r.ortho ? r.ortho.sub : 'Not returned by the check'}</div>
        </div>
        <div>
          <div className="elr-lab">Plan year</div>
          <div className="elr-v">{r.planYear ? r.planYear.range : 'Not on file'}</div>
          <div className="elr-s">{r.planYear ? r.planYear.year : ''}</div>
        </div>
      </div>
    </>
  );
}

function Opportunities({ r }: { r: ReportModel }) {
  if (r.opportunities.length === 0) return null;
  return (
    <>
      <h2>Worth talking about</h2>
      <div>
        {r.opportunities.map((o) => (
          <div className="elr-orow" key={o.key}>
            <div className="elr-ol">
              <b>{o.title}</b>
              <span className="elr-d">{o.why}</span>
            </div>
            <div className="elr-amt">
              <span className="elr-lab">{o.label}</span>
              <br />
              <span className="elr-v">{o.amount}</span>
            </div>
          </div>
        ))}
      </div>
      <p className="elr-src">
        Age limits and what the plan has left are already applied, so nothing here is offered that {r.patientName} cannot
        use.
        {r.hiddenCount > 0
          ? ` ${countWord(r.hiddenCount)} more ${r.hiddenCount === 1 ? 'was' : 'were'} left out by this practice’s settings.`
          : ''}
      </p>
    </>
  );
}

function Sources({ r }: { r: ReportModel }) {
  if (r.sources.length === 0) return null;
  return (
    <>
      <h2>Where these facts came from</h2>
      <div className="elr-srcs">
        {r.sources.map((s) => (
          <div key={s.key} className="elr-srcline">
            <b>{s.title}</b> &mdash; {s.detail}
          </div>
        ))}
      </div>
    </>
  );
}

function Footer({ r }: { r: ReportModel }) {
  const payer = r.plan?.payerShort ?? 'the plan';
  return (
    <footer className="elr-foot">
      <span>
        This is an estimate, not a guarantee of payment. What {payer} finally pays can differ. Saved as snapshot{' '}
        <span className="elr-mono">{r.snapshotId}</span>; the figures above are the ones quoted on {r.preparedLabel}.
      </span>
      <span className="elr-foot-r">
        Prepared by ELVA
        <br />
        {r.preparedLabel}
      </span>
    </footer>
  );
}

function NotFound() {
  return (
    <article className="elr-sheet" aria-labelledby="elr-title">
      <Masthead />
      <h1 id="elr-title">We can’t find this patient</h1>
      <p className="elr-sub">
        This link doesn’t match a patient in ELVA RCM. Open the patient from the Patients list, then print the report
        again.
      </p>
    </article>
  );
}
